import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { settingsAPI } from '../services/api';

// ─── Platform Config ───
// Each platform has: name (DB key), displayName, initials, color, type
// supported = has a working email parser in the backend
const SECTIONS = [
    {
        key: 'us',
        label: 'United States',
        flag: '🇺🇸',
        platforms: [
            { name: 'zillow', displayName: 'Zillow', domain: 'zillow.com', initials: 'Z', color: '#006AFF', type: 'Direct integration' },
            { name: 'realtor', displayName: 'Realtor.com', domain: 'realtor.com', initials: 'R', color: '#D92228', type: 'API connection' },
            { name: 'redfin', displayName: 'Redfin', domain: 'redfin.com', initials: 'Rf', color: '#A02021', type: 'Email parsing' },
            { name: 'trulia', displayName: 'Trulia', domain: 'trulia.com', initials: 'T', color: '#20C063', type: 'Email parsing' },
            { name: 'homes', displayName: 'Homes.com', domain: 'homes.com', initials: 'H', color: '#FB8800', type: 'Email parsing' },
            { name: 'apartments', displayName: 'Apartments.com', domain: 'apartments.com', initials: 'Ap', color: '#272B31', type: 'Email parsing' },
            { name: 'rent', displayName: 'Rent.com', domain: 'rent.com', initials: 'Rn', color: '#FF4F00', type: 'Email parsing' },
            { name: 'zumper', displayName: 'Zumper', domain: 'zumper.com', initials: 'Zu', color: '#2D3A8C', type: 'Email parsing' },
            { name: 'hotpads', displayName: 'HotPads', domain: 'hotpads.com', initials: 'Hp', color: '#EB4837', type: 'Email parsing' },
            { name: 'homefinder', displayName: 'HomeFinder', domain: 'homefinder.com', initials: 'Hf', color: '#F64438', type: 'Email parsing' },
            { name: 'movoto', displayName: 'Movoto', domain: 'movoto.com', initials: 'Mv', color: '#D61E28', type: 'Email parsing' },
            { name: 'century21', displayName: 'Century 21', domain: 'century21.com', initials: 'C21', color: '#BEAF87', type: 'Email parsing' },
            { name: 'coldwellbanker', displayName: 'Coldwell Banker', domain: 'coldwellbanker.com', initials: 'CB', color: '#002349', type: 'Email parsing' },
            { name: 'sothebysrealty', displayName: 'Sotheby\'s', domain: 'sothebysrealty.com', initials: 'SIR', color: '#002349', type: 'Email parsing' },
            { name: 'remax', displayName: 'RE/MAX', domain: 'remax.com', initials: 'Rx', color: '#DC1C2E', type: 'Email parsing' },
            { name: 'kw', displayName: 'Keller Williams', domain: 'kw.com', initials: 'KW', color: '#B40101', type: 'Email parsing' },
            { name: 'compass', displayName: 'Compass', domain: 'compass.com', initials: 'Co', color: '#000000', type: 'Email parsing' },
            { name: 'opendoor', displayName: 'Opendoor', domain: 'opendoor.com', initials: 'Op', color: '#1C69D4', type: 'Email parsing' },
            { name: 'offerpad', displayName: 'Offerpad', domain: 'offerpad.com', initials: 'Of', color: '#F56E21', type: 'Email parsing' },
            { name: 'homesnap', displayName: 'Homesnap', domain: 'homesnap.com', initials: 'Hs', color: '#FFA300', type: 'Email parsing' },
            { name: 'homelight', displayName: 'HomeLight', domain: 'homelight.com', initials: 'HL', color: '#0F2D52', type: 'Email parsing' },
            { name: 'loopnet', displayName: 'LoopNet', domain: 'loopnet.com', initials: 'Ln', color: '#D11241', type: 'Email parsing' },
            { name: 'costar', displayName: 'CoStar', domain: 'costar.com', initials: 'Cs', color: '#1A4064', type: 'Email parsing' },
            { name: 'atomicmail', displayName: 'AtomicMail', domain: 'atomic.vc', initials: 'AM', color: '#8B5CF6', type: 'Beta Integration' },
        ],
    },
    {
        key: 'uk',
        label: 'United Kingdom',
        flag: '🇬🇧',
        platforms: [
            { name: 'rightmove', displayName: 'Rightmove', domain: 'rightmove.co.uk', initials: 'Rm', color: '#26D0CE', type: 'Direct integration' },
            { name: 'zoopla', displayName: 'Zoopla', domain: 'zoopla.co.uk', initials: 'Zp', color: '#522467', type: 'Email parsing' },
            { name: 'onthemarket', displayName: 'OnTheMarket', domain: 'onthemarket.com', initials: 'OtM', color: '#FD4C5D', type: 'API connection' },
            { name: 'primelocation', displayName: 'PrimeLocation', domain: 'primelocation.com', initials: 'PL', color: '#2D3A8C', type: 'Email parsing' },
        ],
    },
    {
        key: 'india',
        label: 'India',
        flag: '🇮🇳',
        platforms: [
            { name: '99acres', displayName: '99acres', domain: '99acres.com', initials: '99', color: '#0078DB', type: 'Email parsing' },
            { name: 'magicbricks', displayName: 'MagicBricks', domain: 'magicbricks.com', initials: 'MB', color: '#D8232A', type: 'API connection' },
            { name: 'housing', displayName: 'Housing.com', domain: 'housing.com', initials: 'Hc', color: '#E8453C', type: 'Email parsing' },
        ],
    },
    {
        key: 'uae',
        label: 'UAE',
        flag: '🇦🇪',
        platforms: [
            { name: 'propertyfinder', displayName: 'PropertyFinder', domain: 'propertyfinder.ae', initials: 'PF', color: '#EF4056', type: 'API connection' },
            { name: 'bayut', displayName: 'Bayut', domain: 'bayut.com', initials: 'By', color: '#00A651', type: 'Email parsing' },
            { name: 'dubizzle', displayName: 'Dubizzle', domain: 'dubizzle.com', initials: 'Db', color: '#E53238', type: 'Email parsing' },
        ],
    },
    {
        key: 'australia',
        label: 'Australia',
        flag: '🇦🇺',
        platforms: [
            { name: 'realestate_au', displayName: 'Realestate.com.au', domain: 'realestate.com.au', initials: 'Rea', color: '#E4002B', type: 'Direct integration' },
            { name: 'domain', displayName: 'Domain', domain: 'domain.com.au', initials: 'Do', color: '#36B37E', type: 'Email parsing' },
        ],
    },
];

// Map DB country codes to our section keys
const COUNTRY_KEY_MAP = {
    'US': 'us',
    'UNITED STATES': 'us',
    'USA': 'us',
    'GB': 'uk',
    'UK': 'uk',
    'UNITED KINGDOM': 'uk',
    'IN': 'india',
    'INDIA': 'india',
    'AE': 'uae',
    'UNITED ARAB EMIRATES': 'uae',
    'AU': 'australia',
    'AUSTRALIA': 'australia'
};

const PlatformIcon = ({ platform }) => {
    const [logoUrl, setLogoUrl] = useState(`https://logo.clearbit.com/${platform.domain}?size=128`);
    const [errorCount, setErrorCount] = useState(0);

    const handleLogoError = () => {
        if (errorCount === 0) {
            // Try Google favicon service as fallback
            setLogoUrl(`https://www.google.com/s2/favicons?domain=${platform.domain}&sz=128`);
            setErrorCount(1);
        } else {
            // Fallback to initials
            setErrorCount(2);
        }
    };

    if (platform.domain && errorCount < 2) {
        return (
            <div className="w-14 h-14 bg-white dark:bg-transparent rounded-xl shadow-sm border border-teal/10 dark:border-light/10 flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
                <img
                    key={logoUrl}
                    src={logoUrl}
                    alt={platform.displayName}
                    className="w-full h-full object-contain p-2"
                    onError={handleLogoError}
                    loading="eager"
                />
            </div>
        );
    }

    return (
        <div
            className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-md transition-transform group-hover:scale-105"
            style={{ backgroundColor: platform.color }}
        >
            {platform.initials}
        </div>
    );
};

const LeadSources = () => {
    const [enabledSources, setEnabledSources] = useState([]);
    const [originalSources, setOriginalSources] = useState([]);
    const [leadCounts, setLeadCounts] = useState({});
    const [expandedSections, setExpandedSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // User data
    const [userCountryKey, setUserCountryKey] = useState(null);
    const [subscription, setSubscription] = useState({ name: 'Starter', limit: 5, isUnlimited: false }); // Default fallback

    const hasChanges = JSON.stringify([...enabledSources].sort()) !== JSON.stringify([...originalSources].sort());

    // Auto-dismiss toasts
    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 4000);
            return () => clearTimeout(t);
        }
    }, [toast]);

    // Fetch on mount
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [sourcesRes, statsRes, profileRes, usageRes] = await Promise.allSettled([
                settingsAPI.getLeadSources(),
                settingsAPI.getLeadSourceStats(),
                settingsAPI.getProfile(),
                settingsAPI.getUsage()
            ]);

            // 1. Enabled Sources
            if (sourcesRes.status === 'fulfilled') {
                const enabled = (sourcesRes.value.data?.sources || [])
                    .filter(s => s.is_enabled)
                    .map(s => s.platform_name);
                setEnabledSources(enabled);
                setOriginalSources(enabled);
            }

            // 2. Lead Counts
            if (statsRes.status === 'fulfilled') {
                setLeadCounts(statsRes.value.data?.counts || {});
            }

            // 3. User Profile (for country)
            let country = 'us'; // Default fallback
            if (profileRes.status === 'fulfilled') {
                const user = profileRes.value.data?.user;
                if (user?.default_country) {
                    const mapped = COUNTRY_KEY_MAP[user.default_country.toUpperCase()];
                    if (mapped) country = mapped;
                }
            }
            setUserCountryKey(country);
            // Auto-expand user's country on load
            setExpandedSections([country]);

            // 4. Plan & Usage
            if (usageRes.status === 'fulfilled') {
                const usageData = usageRes.value.data?.usage;
                if (usageData) {
                    // Use the real lead_sources limit from the backend
                    const limit = usageData.lead_sources?.limit ?? 5;

                    setSubscription({
                        name: usageData.plan_name || 'Starter',
                        limit: limit === -1 ? Infinity : limit,
                        isUnlimited: limit === -1
                    });
                }
            }

        } catch (err) {
            console.error('Error fetching lead sources:', err);
        } finally {
            setLoading(false);
        }
    };

    const sortedSections = useMemo(() => {
        let sections = [...SECTIONS];

        // 1. Filter by search
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            sections = sections.map(section => {
                const matchingPlatforms = section.platforms.filter(p =>
                    p.displayName.toLowerCase().includes(query) ||
                    section.label.toLowerCase().includes(query)
                );
                // Keep section if label matches OR it has matching platforms
                if (section.label.toLowerCase().includes(query) || matchingPlatforms.length > 0) {
                    return { ...section, platforms: matchingPlatforms.length ? matchingPlatforms : section.platforms };
                }
                return null;
            }).filter(Boolean);
        }

        // 2. Sort: User country first
        if (userCountryKey) {
            sections.sort((a, b) => {
                if (a.key === userCountryKey) return -1;
                if (b.key === userCountryKey) return 1;
                return 0; // Keep existing order for others
            });
        }

        return sections;
    }, [searchQuery, userCountryKey]);

    // Auto-expand on search
    useEffect(() => {
        if (searchQuery.trim()) {
            const allKeys = sortedSections.map(s => s.key);
            setExpandedSections(allKeys);
        }
    }, [searchQuery, sortedSections]);

    const handleToggle = useCallback((platformName) => {
        setEnabledSources(prev => {
            if (prev.includes(platformName)) {
                return prev.filter(p => p !== platformName);
            }
            if (prev.length >= subscription.limit) {
                setToast({ type: 'error', text: `Maximum ${subscription.limit} sources allowed on your ${subscription.name} plan.` });
                return prev;
            }
            return [...prev, platformName];
        });
    }, [subscription]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsAPI.updateLeadSources({ enabledPlatforms: enabledSources });
            setOriginalSources([...enabledSources]);
            setToast({ type: 'success', text: `Saved! ${enabledSources.length} sources enabled.` });
        } catch (err) {
            console.error('Error saving lead sources:', err);
            setToast({ type: 'error', text: err?.response?.data?.error || 'Failed to save changes.' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEnabledSources([...originalSources]);
    };

    const toggleSection = (key) => {
        setExpandedSections(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const enabledCount = enabledSources.length;
    const usagePercent = subscription.isUnlimited ? 0 : Math.min(Math.round((enabledCount / subscription.limit) * 100), 100);

    // ─── Loading Skeleton ───
    if (loading) {
        return (
            <div className="bg-mint dark:bg-navy min-h-screen">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                    <div>
                        <div className="h-8 w-48 bg-teal/10 dark:bg-light/10 rounded-lg animate-pulse mb-2"></div>
                        <div className="h-4 w-72 bg-teal/5 dark:bg-light/5 rounded-lg animate-pulse"></div>
                    </div>
                    <div className="w-64 h-16 bg-teal/5 dark:bg-light/5 rounded-lg animate-pulse"></div>
                </div>
                {[1, 2, 3].map(i => (
                    <div key={i} className="mb-6">
                        <div className="h-6 w-40 bg-teal/10 dark:bg-light/10 rounded mb-4 animate-pulse"></div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[1, 2, 3].map(j => (
                                <div key={j} className="h-44 bg-teal/5 dark:bg-light/5 rounded-xl animate-pulse"></div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="bg-mint dark:bg-navy min-h-screen pb-24">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-20 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-bold flex items-center gap-2 ${toast.type === 'success'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                    <span className="material-symbols-outlined text-lg">
                        {toast.type === 'success' ? 'check_circle' : 'error'}
                    </span>
                    {toast.text}
                </div>
            )}

            {/* ─── Header ─── */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-darkTeal dark:text-light mb-2 text-left">Lead Sources</h2>
                    <p className="text-darkTeal/60 dark:text-light/60 text-sm text-left max-w-lg">
                        Enable platforms to automatically import leads into your pipeline.
                        {subscription.isUnlimited ? 'Your plan allows unlimited sources.' : `Your plan allows up to ${subscription.limit} sources.`}
                    </p>

                    {/* Search Input */}
                    <div className="mt-6 relative max-w-sm">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-darkTeal/40 dark:text-light/40 material-symbols-outlined">search</span>
                        <input
                            type="text"
                            placeholder="Search countries or platforms..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-navy border border-teal/10 dark:border-light/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-cyan text-darkTeal dark:text-light placeholder-darkTeal/30 dark:placeholder-light/30 transition-shadow shadow-sm"
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-3">
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="px-4 py-2 bg-white dark:bg-navy border border-teal/30 dark:border-cyan/30 text-teal dark:text-cyan hover:bg-teal/5 dark:hover:bg-cyan/10 font-medium rounded-lg transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                    >
                        <span className={`material-symbols-outlined text-base ${loading ? 'animate-spin' : ''}`}>refresh</span>
                        Refresh
                    </button>
                    <div className="w-full md:w-72 bg-white dark:bg-navy border border-teal/20 dark:border-light/10 rounded-xl p-4 shadow-sm">
                        <div className="flex justify-between items-center mb-3">
                            <span className="text-xs font-bold text-darkTeal/60 dark:text-light/60 uppercase tracking-wider">{subscription.name} Plan</span>
                            <span className={`text-xs font-bold ${!subscription.isUnlimited && enabledCount >= subscription.limit ? 'text-red-500' : 'text-primary dark:text-cyan'}`}>
                                {enabledCount}/{subscription.isUnlimited ? '∞' : subscription.limit} Enabled
                            </span>
                        </div>
                        <div className="w-full h-2.5 bg-teal/5 dark:bg-light/5 rounded-full overflow-hidden mb-1">
                            <div
                                className={`h-full rounded-full transition-all duration-500 ${!subscription.isUnlimited && enabledCount >= subscription.limit ? 'bg-red-500' : 'bg-primary dark:bg-cyan'
                                    }`}
                                style={{ width: subscription.isUnlimited ? '100%' : `${usagePercent}%` }}
                            ></div>
                        </div>
                        {!subscription.isUnlimited && enabledCount >= subscription.limit && (
                            <p className="text-[10px] text-red-500 mt-1 text-right">Limit reached. Upgrade for more.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Country Sections ─── */}
            <div className="space-y-6">
                {sortedSections.length === 0 ? (
                    <div className="text-center py-12 opacity-50">
                        <span className="material-symbols-outlined text-4xl mb-2">search_off</span>
                        <p>No platforms match your search.</p>
                    </div>
                ) : (
                    sortedSections.map(section => {
                        const isExpanded = expandedSections.includes(section.key);
                        const activeCount = section.platforms.filter(p => enabledSources.includes(p.name)).length;

                        return (
                            <div key={section.key} className="bg-transparent">
                                {/* Section Header */}
                                <button
                                    onClick={() => toggleSection(section.key)}
                                    className="w-full flex items-center justify-between group py-3 px-2 rounded-lg hover:bg-teal/5 dark:hover:bg-light/5 transition-colors mb-2"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl shadow-sm">{section.flag}</span>
                                        <h3 className="text-xl font-bold text-darkTeal dark:text-light group-hover:text-primary dark:group-hover:text-cyan transition-colors text-left">
                                            {section.label}
                                        </h3>
                                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${activeCount > 0
                                            ? 'bg-primary/10 dark:bg-cyan/20 text-primary dark:text-cyan'
                                            : 'bg-teal/10 dark:bg-light/10 text-darkTeal/50 dark:text-light/50'
                                            }`}>
                                            {activeCount} Active
                                        </span>
                                    </div>
                                    <span className={`material-symbols-outlined text-darkTeal/30 dark:text-light/30 group-hover:text-primary dark:group-hover:text-cyan transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                                        expand_more
                                    </span>
                                </button>

                                {/* Platform Cards Grid */}
                                <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 transition-all duration-500 ease-in-out overflow-hidden ${isExpanded ? 'opacity-100 max-h-[50000px] mb-8' : 'opacity-0 max-h-0'
                                    }`}>
                                    {section.platforms.map(platform => {
                                        const isEnabled = enabledSources.includes(platform.name);
                                        const count = leadCounts[platform.name] || 0;

                                        return (
                                            <div
                                                key={platform.name}
                                                className={`relative bg-white dark:bg-navy rounded-xl p-3 md:p-5 border transition-all duration-300 group hover:shadow-lg ${isEnabled
                                                    ? 'border-primary/40 dark:border-cyan/40 shadow-[0_0_15px_rgba(0,128,128,0.1)] dark:shadow-[0_0_15px_rgba(8,217,214,0.15)]'
                                                    : 'border-teal/15 dark:border-light/10 opacity-70 hover:opacity-100 hover:border-teal/30 dark:hover:border-light/20'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-start mb-3 md:mb-4">
                                                    {/* Platform icon */}
                                                    <div className="scale-75 origin-top-left md:scale-100">
                                                        <PlatformIcon platform={platform} />
                                                    </div>

                                                    {/* Toggle Switch */}
                                                    <button
                                                        onClick={() => handleToggle(platform.name)}
                                                        className={`relative inline-flex h-5 w-9 md:h-6 md:w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${isEnabled
                                                            ? 'bg-primary dark:bg-cyan'
                                                            : 'bg-gray-300 dark:bg-gray-600'
                                                            }`}
                                                    >
                                                        <span
                                                            className={`inline-block h-4 w-4 md:h-5 md:w-5 rounded-full bg-white shadow-md transform transition-transform duration-300 ${isEnabled ? 'translate-x-4 md:translate-x-5' : 'translate-x-0.5'
                                                                }`}
                                                        />
                                                    </button>
                                                </div>

                                                <h4 className="text-sm md:text-lg font-bold text-darkTeal dark:text-light mb-0.5 text-left truncate">{platform.displayName}</h4>
                                                <p className="text-[10px] md:text-xs text-darkTeal/50 dark:text-light/50 mb-3 md:mb-4 text-left font-medium truncate">{platform.type}</p>

                                                {/* Footer: lead count + status dot */}
                                                <div className="flex items-center justify-between pt-3 md:pt-4 border-t border-teal/5 dark:border-light/5">
                                                    {isEnabled ? (
                                                        <>
                                                            <span className="text-xs md:text-sm font-bold text-darkTeal/80 dark:text-light/80">{count} leads</span>
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)] animate-pulse"></span>
                                                                <span className="text-[10px] uppercase font-bold text-green-500/80">Live</span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="text-sm font-medium text-darkTeal/40 dark:text-light/40">Disabled</span>
                                                            <span className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600"></span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* ─── Footer ─── */}
            <div className="mt-8 mb-4 flex items-center justify-between text-xs text-darkTeal/30 dark:text-light/30 border-t border-teal/5 dark:border-light/5 pt-6">
                <p>© 2026 OffTrigger.</p>
                <div className="flex gap-4">
                    <a className="hover:text-primary dark:hover:text-cyan transition-colors" href="#">Privacy</a>
                    <a className="hover:text-primary dark:hover:text-cyan transition-colors" href="#">Terms</a>
                </div>
            </div>

            {/* ─── Sticky Save Bar ─── */}
            {hasChanges && (
                <div className="fixed bottom-16 md:bottom-0 left-0 md:left-64 right-0 bg-white dark:bg-navy border-t border-primary/20 dark:border-cyan/30 p-4 shadow-[0_-5px_20px_rgba(0,0,0,0.15)] dark:shadow-[0_-5px_20px_rgba(0,0,0,0.5)] z-50 animate-slide-up">
                    <div className="flex items-center justify-between max-w-7xl mx-auto px-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 dark:bg-cyan/20 flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary dark:text-cyan text-sm">info</span>
                            </div>
                            <span className="text-darkTeal dark:text-light font-medium text-sm hidden sm:inline">
                                You have <strong className="text-primary dark:text-cyan">{enabledCount} sources enabled</strong> out of {subscription.isUnlimited ? '∞' : subscription.limit}.
                            </span>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handleCancel}
                                className="px-4 py-2 rounded-lg border border-teal/20 dark:border-light/10 text-darkTeal dark:text-light hover:bg-teal/5 dark:hover:bg-light/5 transition-colors font-medium text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-6 py-2 rounded-lg bg-primary dark:bg-cyan text-white dark:text-navy font-bold hover:bg-primary/90 dark:hover:bg-cyan/90 transition-colors shadow-lg shadow-primary/20 dark:shadow-cyan/30 flex items-center gap-2 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <span className="animate-spin material-symbols-outlined text-lg">progress_activity</span>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-lg">save</span>
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadSources;
